import { action, computed, makeObservable, observable, runInAction } from "mobx";
import { getUserInfo } from "shared/api/auth/profileApi";
import { BaseUserProfileRequestData, ProfileInfo, TenantProfileRequestData } from "shared/interfaces/myProfile";
import { format, parseISO } from "date-fns";
import {
    COMMA_SEPARATOR,
    DATE_FORMATS,
    NO_VALUE_PLACEHOLDER,
    ACCOUNT_TYPE_NAMES,
    ISO_DATE_TIME_SPLITTER,
    DEFAULT_TIME,
    DATE_SPLITTER,
} from "shared/constants/appConstants";
import { getEmploymentStatuses } from "shared/api/spaceStone/lookUpApi";
import { AboutForm, EditType, ProfileContacts } from "app/spaceStone/myProfile/interfaces";
import { EmployedStatusLookup } from "app/spaceStone/myProfile/about/interfaces";
import { uniq } from "lodash";
import { AddressesLookup } from "shared/models/spaceStone/lookup/addressesLookup";
import { LocalizationId as RolesLocalization } from "shared/roles.localization";
import { LocalizeFunction } from "shared/hooks/useLocalize";
import formatAddress from "shared/helpers/formatAddress";
import { RootStore } from "app/rootStore";
import { Country } from "components/userDetails/interfaces/country";
import { AxiosError } from "axios";
import { pickServerValidationError } from "shared/api/utils";
import { formatPhoneNumber } from "shared/utils/phone.utils";
import { AccountType } from "shared/constants/enums";
import { BaseLookup } from "shared/models/lookups";

export class MyProfileStore {
    @observable public profileData: ProfileInfo | null = null;
    @observable public editType: EditType = EditType.None;
    @observable public employmentStatuses: EmployedStatusLookup[] = [];
    @observable public userRoles: Array<BaseLookup> = [];
    @observable public changeEmailFormError: string | null = null;
    @observable public phoneNumberFormError: string | null = null;
    @observable public phoneCodeFormError: string | null = null;
    @observable public shouldShowSuccessEmailChangedToast = false;
    @observable public tempAvatar: string | null = null;
    public formState = {};
    private rootStore: RootStore;

    constructor(rootStore: RootStore) {
        makeObservable(this);
        this.rootStore = rootStore;
    }

    @action.bound
    public clearStore(): void {
        this.profileData = null;
    }

    @action.bound
    public async requestUserProfile(): Promise<void> {
        const profileInfo = await getUserInfo();
        runInAction(() => {
            this.profileData = profileInfo;
            this.tempAvatar = this.avatar;
            this.rootStore.setAccountTypeIds = profileInfo.accountTypeIds;
            this.userRoles = profileInfo.additionalDetails.customRoles || [];
        });
    }

    @action.bound
    public async requestEmploymentStatuses(localize: LocalizeFunction): Promise<void> {
        const data = await getEmploymentStatuses();
        runInAction(() => {
            this.employmentStatuses = data.map(employment => ({ id: employment.id, text: localize(employment.text) }));
        });
    }

    @action.bound
    public clearChangeEmailError(): void {
        this.changeEmailFormError = null;
    }

    @action.bound
    public setChangeEmailError(error: AxiosError): void {
        this.changeEmailFormError = pickServerValidationError(error);
    }

    @action.bound
    public clearChangePhoneFormError(): void {
        this.phoneNumberFormError = null;
        this.phoneCodeFormError = null;
    }

    @action.bound
    public setPhoneNumberError(error: AxiosError): void {
        this.phoneNumberFormError = pickServerValidationError(error);
    }

    @action.bound
    public setPhoneCodeError(error: AxiosError): void {
        this.phoneCodeFormError = pickServerValidationError(error);
    }

    @action.bound
    public setShowSuccessEmailChangedToast(value: boolean): void {
        this.shouldShowSuccessEmailChangedToast = value;
    }

    @action.bound
    public updateProfilePhoneNumber(phoneNumber: string): void {
        this.profileData!.additionalDetails.phoneNumber = phoneNumber;
    }

    @action.bound
    public setEditType(type: EditType): void {
        this.editType = type;
    }

    @action.bound
    public resetEditType(): void {
        this.editType = EditType.None;
        this.formState = {};
        this.tempAvatar = this.avatar;
    }

    @action.bound
    public setAboutFormState(state: AboutForm): void {
        const dateOfBirth =
            state.dateOfBirth instanceof Date
                ? `${format(
                      parseISO(state.dateOfBirth.toISOString()),
                      DATE_FORMATS.FILTER_FORMAT
                  )}${ISO_DATE_TIME_SPLITTER}${DEFAULT_TIME}`
                : this.profileData?.additionalDetails.dateOfBirth;

        this.formState = {
            dateOfBirth,
            marketingPreferences: state.marketingPreferences,
            employmentStatus: state.employmentStatus,
        };
    }

    @action.bound
    public setAboutFormStateAdmin(state: AboutForm): void {
        this.formState = {
            firstName: state.firstName,
            lastName: state.lastName,
        };
    }

    @action.bound
    public setAddressFormState(state: AddressesLookup): void {
        this.formState = {
            ...this.profileData?.additionalDetails.address,
            addressLine1: state.addressLine1,
            addressLine2: state.addressLine2,
            country: state.country,
            postcode: state.postcode,
            city: state.city,
            latitude: state.latitude,
            longitude: state.longitude,
        } as AddressesLookup;
    }

    @action.bound
    private getAddressUserData(): AddressesLookup | null {
        if (this.profileData) {
            return this.profileData.additionalDetails.address!;
        }

        return null;
    }

    @action.bound
    public prepareDataForUpdateAboutTenant(): TenantProfileRequestData {
        const { employmentStatus, dateOfBirth } = this.formState as AboutForm;

        return {
            userProfile: {
                ...this.prepareDataForUpdateCommonProfile().userProfile,
                address: this.getAddressUserData(),
                dateOfBirth: dateOfBirth || undefined,
                employmentStatus,
            },
        };
    }

    @action.bound
    public prepareDataForUpdateAboutAdmin(): BaseUserProfileRequestData {
        const { firstName, lastName } = this.formState as AboutForm;

        return {
            userProfile: {
                ...this.getBaseUserData(),
                firstName,
                lastName,
                photoBase64: this.tempAvatar,
                marketingPreferences: false,
            },
        };
    }

    @action.bound
    public prepareDataForUpdateAddressTenant(): TenantProfileRequestData {
        const {
            addressLine1,
            addressLine2,
            city,
            postcode,
            country,
            latitude,
            longitude,
            uniquePropertyReferenceNumber,
        } = this.formState as AddressesLookup;

        return {
            userProfile: {
                ...this.getBaseUserData(),
                ...this.getAboutUserData(),
                address: {
                    addressLine1,
                    addressLine2,
                    city,
                    postcode,
                    latitude,
                    longitude,
                    uniquePropertyReferenceNumber,
                    country: country as Country,
                },
            },
        };
    }

    @action.bound
    public prepareDataForUpdateCommonProfile(): BaseUserProfileRequestData {
        const { marketingPreferences } = this.formState as AboutForm;

        const userProfile = {
            ...this.getBaseUserData(),
            marketingPreferences,
            photoBase64: this.tempAvatar,
        };

        return {
            userProfile,
        };
    }

    @action.bound
    private getAboutUserData() {
        const {
            additionalDetails: { marketingPreferences, employmentStatus, dateOfBirth },
        } = this.profileData!;

        return { marketingPreferences, employmentStatus, dateOfBirth };
    }

    @action.bound
    private getBaseUserData() {
        const {
            id,
            email,
            additionalDetails: { phoneNumber, firstName, lastName, photoBase64, dateOfBirth },
        } = this.profileData!;

        return {
            userProfileID: +id,
            phone: phoneNumber || null,
            photoBase64: photoBase64 || null,
            email,
            firstName,
            lastName,
            dateOfBirth,
        };
    }

    @action.bound
    public roleLabels(localize: LocalizeFunction): string[] {
        const { accountTypeIds } = this.profileData!;

        return uniq(
            accountTypeIds.split(COMMA_SEPARATOR).map(accountTypeId => {
                const parserAccountTypeId = Number(accountTypeId);
                if (Number.isNaN(parserAccountTypeId)) {
                    return NO_VALUE_PLACEHOLDER;
                }
                if (
                    parserAccountTypeId === AccountType.CorporateLandlord ||
                    parserAccountTypeId === AccountType.Agent
                ) {
                    return localize(RolesLocalization.Admin);
                }

                if (parserAccountTypeId === AccountType.PrivateLandlord) {
                    return localize(RolesLocalization.Landlord);
                }
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                return localize(ACCOUNT_TYPE_NAMES[parserAccountTypeId]);
            })
        );
    }

    @action.bound
    public getEmploymentStatusLookup(localize: LocalizeFunction): { id: number; text: string } {
        const {
            additionalDetails: { employmentStatus },
        } = this.profileData!;

        return {
            id: employmentStatus ? +employmentStatus.id : this.employmentStatuses[0].id,
            text: localize(employmentStatus ? employmentStatus.text : this.employmentStatuses[0].text),
        };
    }

    @action.bound
    public setNewAvatar(image: string): void {
        this.tempAvatar = image;
    }

    @action.bound
    public deleteAvatar(): void {
        this.tempAvatar = null;
    }

    @computed get addressDefaultValues(): AddressesLookup {
        const {
            additionalDetails: { address },
        } = this.profileData!;

        if (address) {
            return {
                ...address,
                addressLine2: address.addressLine2 || "",
            };
        }

        return {
            addressLine1: "",
            addressLine2: "",
            city: "",
            postcode: "",
            country: undefined,
            latitude: undefined,
            longitude: undefined,
            uniquePropertyReferenceNumber: "",
        };
    }

    @computed get id(): number {
        const { id } = this.profileData!;

        return +id;
    }

    @computed get avatar(): string | null {
        if (this.profileData && this.profileData.additionalDetails) {
            return this.profileData.additionalDetails.photoBase64 || null;
        }

        return null;
    }

    @computed get firstName(): string {
        if (this.profileData && this.profileData.additionalDetails) {
            return this.profileData.additionalDetails.firstName || NO_VALUE_PLACEHOLDER;
        }

        return NO_VALUE_PLACEHOLDER;
    }

    @computed get lastName(): string {
        if (this.profileData && this.profileData.additionalDetails) {
            return this.profileData.additionalDetails.lastName || NO_VALUE_PLACEHOLDER;
        }

        return NO_VALUE_PLACEHOLDER;
    }

    @computed get fullName(): string {
        if (this.profileData) {
            const {
                additionalDetails: { firstName, lastName },
            } = this.profileData;

            if (firstName && lastName) {
                return `${firstName} ${lastName}`;
            }

            return NO_VALUE_PLACEHOLDER;
        }

        return NO_VALUE_PLACEHOLDER;
    }

    @computed get formattedBirthDay(): string {
        if (this.profileData && this.profileData.additionalDetails.dateOfBirth) {
            try {
                const [date] = (this.profileData.additionalDetails.dateOfBirth as string).split(ISO_DATE_TIME_SPLITTER);
                const [year, month, day] = date.split(DATE_SPLITTER);
                return `${day}/${month}/${year}`;
            } catch (e) {
                return NO_VALUE_PLACEHOLDER;
            }
        }

        return NO_VALUE_PLACEHOLDER;
    }

    @computed get birthDay(): Date | null {
        if (this.profileData && this.profileData.additionalDetails.dateOfBirth) {
            return new Date(this.profileData.additionalDetails.dateOfBirth);
        }

        return null;
    }

    @computed get hasBirthDayValue(): boolean {
        if (this.profileData) {
            return Boolean(this.profileData.additionalDetails.dateOfBirth);
        }

        return false;
    }

    @computed get email(): string {
        if (this.profileData) {
            return this.profileData.email;
        }

        return "";
    }

    @computed get phoneNumber(): string {
        if (this.profileData && this.profileData.additionalDetails.phoneNumber) {
            return formatPhoneNumber(this.profileData.additionalDetails.phoneNumber);
        }

        return NO_VALUE_PLACEHOLDER;
    }

    @computed get marketingPreferences(): boolean {
        if (this.profileData) {
            return this.profileData.additionalDetails.marketingPreferences;
        }

        return false;
    }

    @computed get address(): string {
        const {
            additionalDetails: { address },
        } = this.profileData!;

        if (address) {
            return formatAddress(address);
        }

        return NO_VALUE_PLACEHOLDER;
    }

    @computed get employmentStatus(): string | null | undefined {
        if (this.profileData) {
            const {
                additionalDetails: { employmentStatus },
            } = this.profileData;

            return employmentStatus?.text;
        }

        return "";
    }

    @computed get isEditMode(): boolean {
        return this.editType !== EditType.None;
    }

    @computed get profileContacts(): ProfileContacts {
        if (this.profileData) {
            return {
                email: this.profileData.email,
                phoneNumber: this.profileData.additionalDetails.phoneNumber,
                fullName: `${this.profileData.additionalDetails.firstName} ${this.profileData.additionalDetails.lastName}`,
            };
        }
        return {
            fullName: "",
            email: "",
            phoneNumber: "",
        };
    }

    @computed get profileCompanyId(): string {
        if (this.profileData) {
            return this.profileData.companyId;
        }

        return "";
    }

    @computed get hasAllRequiredProfileData(): boolean {
        return this.hasAllRequiredAboutProfileData && this.hasAllRequiredContactsData;
    }

    @computed get hasAllRequiredAboutProfileData(): boolean {
        return (
            Boolean(this.profileData?.additionalDetails.firstName) &&
            Boolean(this.profileData?.additionalDetails.lastName)
        );
    }

    @computed get hasAllRequiredContactsData(): boolean {
        return Boolean(this.profileData?.additionalDetails.phoneNumber);
    }

    @computed get userRolesAsList(): string {
        return this.userRoles.map(({ text }) => text).join(", ");
    }
}
